import React, { useState, useEffect, useCallback } from 'react';
import { Warehouse, Plus, RefreshCw, Edit2, Check, X, Trash2, ArrowRightCircle, RotateCcw, AlertTriangle, Lock } from 'lucide-react';
import apiClient from '../../api/apiClient';
import { StockItem, ShowNotificationFn, SALE_STATUS_LABELS } from './crm.types';
import { CrmEmailSyncBanner } from './CrmEmailSyncBanner';

type StockTab = 'transit' | 'effective' | 'out';

interface PendingReceiveItem {
  id: number;
  item_title: string;
  added_at?: string;
  purchase_price?: number;
}

interface PendingReceive {
  id: number;
  item_title: string;
  created_at?: string;
  matching_items: PendingReceiveItem[];
}

function formatPrice(n?: number): string {
  if (n == null) return '—';
  return n.toFixed(2) + ' €';
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

function stockLineAcquisitionTotal(i: StockItem): number {
  return (i.purchase_price || 0) + (i.shipping_cost || 0) + (i.protection_fee || 0);
}

// ── Modal ajout ──────────────────────────────────────────────────────────────

interface AddStockModalProps {
  onClose: () => void;
  onSaved: () => void;
  initialStatus?: string;
}

const AddStockModal: React.FC<AddStockModalProps> = ({ onClose, onSaved, initialStatus = 'in_stock' }) => {
  const [form, setForm] = useState({ item_title: '', item_size: '', item_brand: '', item_condition: '', purchase_price: '', target_sell_price: '', location: '', notes: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiClient.post('/crm/stock', {
        ...form,
        purchase_price: form.purchase_price ? parseFloat(form.purchase_price) : undefined,
        target_sell_price: form.target_sell_price ? parseFloat(form.target_sell_price) : undefined,
        status: initialStatus,
      });
      onSaved(); onClose();
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">Ajouter au stock</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
            <input type="text" required className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.item_title} onChange={e => setForm(f => ({ ...f, item_title: e.target.value }))} placeholder="Ex: Robe H&M" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            {(['item_size', 'item_brand', 'item_condition'] as const).map(field => (
              <div key={field}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field === 'item_size' ? 'Taille' : field === 'item_brand' ? 'Marque' : 'État'}
                </label>
                <input type="text" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))} />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prix d&apos;achat</label>
              <input type="number" step="0.01" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.purchase_price} onChange={e => setForm(f => ({ ...f, purchase_price: e.target.value }))} placeholder="10.00" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prix de revente visé</label>
              <input type="number" step="0.01" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.target_sell_price} onChange={e => setForm(f => ({ ...f, target_sell_price: e.target.value }))} placeholder="20.00" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Localisation</label>
            <input type="text" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="Ex: Étagère B3, Carton cuisine" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50">Annuler</button>
            <button type="submit" disabled={loading} className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
              {loading ? 'Enregistrement...' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Carte stock ──────────────────────────────────────────────────────────────

interface StockCardProps {
  item: StockItem;
  variant: StockTab;
  selected: boolean;
  onToggleSelect: (id: number) => void;
  onUpdate: (id: number, data: Partial<StockItem>) => void;
  onDelete: (id: number) => void;
}

const StockCard: React.FC<StockCardProps> = ({
  item, variant, selected, onToggleSelect, onUpdate, onDelete,
}) => {
  const [editingPrice, setEditingPrice] = useState(false);
  const [targetPrice, setTargetPrice] = useState(String(item.target_sell_price || ''));
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleVal, setTitleVal] = useState(item.item_title || '');
  const canEditTitle = variant === 'effective' || variant === 'out';
  const canEditPrice = variant === 'transit' || variant === 'effective';
  const totalCost = (item.purchase_price || 0) + (item.shipping_cost || 0) + (item.protection_fee || 0);
  const margin = item.target_sell_price && totalCost > 0
    ? item.target_sell_price - totalCost
    : null;

  const saleStatusInfo = item.linked_sale
    ? (SALE_STATUS_LABELS[item.linked_sale.status] || { label: item.linked_sale.status, color: 'text-gray-600', bg: 'bg-gray-100' })
    : null;

  return (
    <div className={`bg-white border rounded-xl shadow-sm transition-all flex items-start gap-2 px-3 pt-2.5 pb-2 ${
      selected ? 'border-blue-400 ring-1 ring-blue-200' : 'border-gray-100'
    }`}>
      <input
        type="checkbox"
        checked={selected}
        onChange={() => onToggleSelect(item.id)}
        className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-blue-600 cursor-pointer flex-shrink-0"
      />

      {item.item_image_url ? (
        <img src={item.item_image_url} alt="" className="w-11 h-11 rounded-lg object-cover flex-shrink-0" />
      ) : (
        <div className="w-11 h-11 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
          <Warehouse size={18} className="text-blue-500" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        {editingTitle ? (
          <div className="flex items-center gap-1 mb-0.5">
            <input
              type="text"
              autoFocus
              className="flex-1 border border-blue-300 rounded-lg px-2 py-0.5 text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={titleVal}
              onChange={e => setTitleVal(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') { onUpdate(item.id, { item_title: titleVal }); setEditingTitle(false); }
                if (e.key === 'Escape') { setTitleVal(item.item_title || ''); setEditingTitle(false); }
              }}
            />
            <button type="button" onClick={() => { onUpdate(item.id, { item_title: titleVal }); setEditingTitle(false); }} className="text-blue-600"><Check size={13} /></button>
            <button type="button" onClick={() => { setTitleVal(item.item_title || ''); setEditingTitle(false); }} className="text-gray-400"><X size={13} /></button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => canEditTitle && setEditingTitle(true)}
            className={`font-semibold text-gray-900 text-[13px] leading-tight text-left flex items-center gap-1 group ${canEditTitle ? 'hover:text-blue-700' : ''}`}
          >
            <span className="truncate">{item.item_title}</span>
            {canEditTitle && <Edit2 size={10} className="opacity-0 group-hover:opacity-50 transition-opacity flex-shrink-0" />}
          </button>
        )}

        {variant === 'out' && item.linked_sale && saleStatusInfo && (
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${saleStatusInfo.bg} ${saleStatusInfo.color}`}>
              Vente · {saleStatusInfo.label}
            </span>
            <span className="text-[10px] text-gray-500 truncate max-w-[200px]">
              {item.linked_sale.item_title}
            </span>
            <span className="text-[10px] font-semibold text-blue-700">{formatPrice(item.linked_sale.sale_price)}</span>
          </div>
        )}

        <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500 flex-wrap">
          {item.item_brand && <span>{item.item_brand}</span>}
          {item.item_size && <span>· {item.item_size}</span>}
          {item.item_condition && <span>· {item.item_condition}</span>}
          {item.location && <span>· 📍 {item.location}</span>}
        </div>

        <div className="flex flex-col gap-0.5 mt-1.5 sm:flex-row sm:flex-wrap sm:gap-3 sm:items-center">
          {item.purchase_price != null && (
            <div className="text-xs flex flex-col sm:flex-row sm:items-baseline sm:gap-1.5">
              <span className="flex items-baseline gap-1.5">
                <span className="text-gray-500">Acheté :</span>
                <span className="font-semibold text-gray-800">{formatPrice(totalCost)}</span>
              </span>
              {(item.shipping_cost || item.protection_fee) && (
                <span className="text-gray-400 text-[10px] leading-relaxed">
                  {formatPrice(item.purchase_price)} art.
                  {item.shipping_cost ? ` + ${formatPrice(item.shipping_cost)} livr.` : ''}
                  {item.protection_fee ? ` + ${formatPrice(item.protection_fee)} prot.` : ''}
                </span>
              )}
            </div>
          )}

          {canEditPrice && (
            <div className="flex items-center gap-1 text-xs">
              <span className="text-gray-500">Revente visée :</span>
              {editingPrice ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number" step="0.01"
                    className="w-16 border border-blue-300 rounded-lg px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={targetPrice}
                    onChange={e => setTargetPrice(e.target.value)}
                    autoFocus
                  />
                  <button type="button" onClick={() => { onUpdate(item.id, { target_sell_price: parseFloat(targetPrice) || undefined }); setEditingPrice(false); }}
                    className="text-blue-600"><Check size={13} /></button>
                  <button type="button" onClick={() => setEditingPrice(false)} className="text-gray-400"><X size={13} /></button>
                </div>
              ) : (
                <button type="button" onClick={() => setEditingPrice(true)} className="font-medium text-blue-700 hover:underline flex items-center gap-0.5">
                  {formatPrice(item.target_sell_price)} <Edit2 size={11} />
                </button>
              )}
            </div>
          )}

          {margin !== null && canEditPrice && (
            <span className={`text-xs font-semibold ${margin >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {margin >= 0 ? '+' : ''}{margin.toFixed(2)} €
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col items-end justify-between self-stretch flex-shrink-0 min-w-[4.5rem]">
        <span className="text-xs text-gray-400">
          {item.added_at ? formatDate(item.added_at) : '—'}
        </span>
        <button
          type="button"
          onClick={() => onDelete(item.id)}
          className="text-xs text-red-400 hover:text-red-600 flex items-center gap-0.5"
        >
          <Trash2 size={11} /> Supprimer
        </button>
      </div>
    </div>
  );
};

// ── Composant principal ──────────────────────────────────────────────────────

interface CrmStockProps {
  showNotification: ShowNotificationFn;
  onPendingCountChange?: () => void;
  crm_email_active?: boolean;
  is_free_plan?: boolean;
  onUpgrade?: () => void;
}

const TAB_LABELS: Record<StockTab, string> = {
  transit: 'En transit',
  effective: 'En stock',
  out: 'Vendus',
};

const CrmStock: React.FC<CrmStockProps> = ({ showNotification, onPendingCountChange, crm_email_active, is_free_plan, onUpgrade }) => {
  const [tab, setTab] = useState<StockTab>('transit');
  const [itemsTransit, setItemsTransit] = useState<StockItem[]>([]);
  const [itemsEffective, setItemsEffective] = useState<StockItem[]>([]);
  const [itemsOut, setItemsOut] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [pendingReceives, setPendingReceives] = useState<PendingReceive[]>([]);

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const [rTransit, rEff, rOut, rPending] = await Promise.all([
        apiClient.get('/crm/stock?scope=transit'),
        apiClient.get('/crm/stock?scope=effective'),
        apiClient.get('/crm/stock?scope=out'),
        apiClient.get('/crm/stock/pending-receive'),
      ]);
      setItemsTransit(rTransit.data);
      setItemsEffective(rEff.data);
      setItemsOut(rOut.data);
      setPendingReceives(Array.isArray(rPending.data) ? rPending.data : []);
    } catch { showNotification('Erreur', 'Impossible de charger le stock', 'error'); }
    finally { setLoading(false); }
  }, [showNotification]);

  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { setSelected(new Set()); }, [tab]);

  const list = tab === 'transit' ? itemsTransit : tab === 'effective' ? itemsEffective : itemsOut;
  /** Coût total engagé : en transit + en stock (argent « bloqué » sur la trésorerie). */
  const totalLatentStockValue =
    itemsTransit.reduce((s, i) => s + stockLineAcquisitionTotal(i), 0) +
    itemsEffective.reduce((s, i) => s + stockLineAcquisitionTotal(i), 0);

  const handleUpdate = async (id: number, data: Partial<StockItem>) => {
    try {
      await apiClient.patch(`/crm/stock/${id}`, data);
      if ('is_received' in data) {
        await loadItems();
      } else {
        setItemsTransit(prev => prev.map(i => i.id === id ? { ...i, ...data } : i));
        setItemsEffective(prev => prev.map(i => i.id === id ? { ...i, ...data } : i));
        setItemsOut(prev => prev.map(i => i.id === id ? { ...i, ...data } : i));
      }
    } catch { showNotification('Erreur', 'Mise à jour échouée', 'error'); }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.delete(`/crm/stock/${id}`);
      setItemsTransit(prev => prev.filter(i => i.id !== id));
      setItemsEffective(prev => prev.filter(i => i.id !== id));
      setItemsOut(prev => prev.filter(i => i.id !== id));
      setSelected(prev => { const n = new Set(prev); n.delete(id); return n; });
      showNotification('Article supprimé', '', 'success');
    } catch { showNotification('Erreur', 'Suppression échouée', 'error'); }
  };

  const toggleSelect = (id: number) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === list.length && list.length > 0) setSelected(new Set());
    else setSelected(new Set(list.map(i => i.id)));
  };

  // "En transit" → "En stock" (marquer reçu)
  const bulkMarkReceived = async () => {
    if (tab !== 'transit') return;
    if (selected.size === 0) {
      showNotification('Aucune sélection', 'Sélectionne au moins un article à marquer comme reçu.', 'warning');
      return;
    }
    if (!window.confirm(`Marquer ${selected.size} article(s) comme reçu(s) ?`)) return;
    try {
      await Promise.all(Array.from(selected).map(id => apiClient.patch(`/crm/stock/${id}`, { is_received: true })));
      setSelected(new Set());
      await loadItems();
      showNotification('Stock mis à jour', 'Article(s) déplacé(s) vers En stock.', 'success');
    } catch {
      showNotification('Erreur', 'Impossible de mettre à jour le stock', 'error');
    }
  };

  // "Vendus" → "En stock" (remettre)
  const bulkMarkIn = async () => {
    if (tab !== 'out') return;
    if (selected.size === 0) {
      showNotification('Aucune sélection', 'Sélectionne au moins un article à remettre en stock.', 'warning');
      return;
    }
    if (!window.confirm(`Remettre ${selected.size} article(s) en stock ? Les ventes liées perdront le coût d'achat stock.`)) return;
    try {
      await Promise.all(Array.from(selected).map(id => apiClient.patch(`/crm/stock/${id}`, { status: 'in_stock' })));
      setSelected(new Set());
      await loadItems();
      showNotification('Stock mis à jour', 'Article(s) remis en stock.', 'success');
    } catch {
      showNotification('Erreur', 'Impossible de remettre en stock', 'error');
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) {
      showNotification('Aucune sélection', 'Sélectionne des articles à supprimer.', 'warning');
      return;
    }
    const label = tab === 'transit' ? 'en transit' : tab === 'effective' ? 'en stock' : 'vendus';
    const warn = tab === 'out' ? " Les ventes encore liées perdront le coût d'achat issu du stock." : '';
    if (!window.confirm(`Supprimer ${selected.size} article(s) ${label} ?${warn}`)) return;
    await Promise.all(Array.from(selected).map(id => apiClient.delete(`/crm/stock/${id}`).catch(() => {})));
    setSelected(new Set());
    loadItems();
    showNotification('Sélection supprimée', '', 'success');
  };

  const resolvePending = async (pendingId: number, stockItemId: number) => {
    try {
      await apiClient.post(`/crm/stock/pending-receive/${pendingId}/resolve`, { stock_item_id: stockItemId });
      await loadItems();
      onPendingCountChange?.();
      showNotification('Article reçu', 'Passé en stock avec succès.', 'success');
    } catch {
      showNotification('Erreur', 'Impossible de résoudre la livraison', 'error');
    }
  };

  const dismissPending = async (pendingId: number) => {
    try {
      await apiClient.delete(`/crm/stock/pending-receive/${pendingId}`);
      setPendingReceives(prev => prev.filter(p => p.id !== pendingId));
      onPendingCountChange?.();
    } catch {
      showNotification('Erreur', 'Impossible d\'ignorer la livraison', 'error');
    }
  };

  // Premier pending en attente de résolution (affiché dans le popup)
  const activePending = pendingReceives[0] ?? null;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* ── En-tête figé (titre + onglets + barre sélection) ── */}
      <div className="flex-shrink-0 bg-gray-50 px-6 pt-6 pb-3 border-b border-gray-100">
        {/* Titre */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Warehouse size={24} className="text-blue-600" /> Stock
            </h1>
            <p className="text-gray-500 text-sm mt-0.5 flex items-center flex-wrap gap-1">
              <span>
                {itemsTransit.length > 0 ? `${itemsTransit.length} en transit · ` : ''}
                {itemsEffective.length} en stock
                {totalLatentStockValue > 0
                  ? ` · valeur engagée (transit + en stock) ${totalLatentStockValue.toFixed(2)} €`
                  : ''}
                {itemsOut.length > 0 ? ` · ${itemsOut.length} vendu${itemsOut.length > 1 ? 's' : ''}` : ''}
              </span>
              {is_free_plan && (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${list.length >= 10 ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-700'}`}>
                  {Math.min(list.length, 10)} / 10
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-col items-stretch sm:items-end gap-2 w-full sm:w-auto min-w-0 max-w-xl">
            {!crm_email_active && <CrmEmailSyncBanner />}
            <div className="flex items-center justify-end gap-2 flex-shrink-0">
              <button type="button" onClick={loadItems} className="p-2 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50">
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              </button>
              <button type="button" onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-sm font-semibold shadow hover:opacity-90">
                <Plus size={16} /> Ajouter
              </button>
            </div>
          </div>
        </div>

        {/* Onglets */}
        <div className="flex items-center gap-0.5 bg-white border border-gray-200 rounded-xl p-1 shadow-sm w-fit mb-3">
          {(['transit', 'effective', 'out'] as StockTab[]).map(t => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`text-sm px-4 py-1.5 rounded-lg font-medium transition-all ${
                tab === t ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {TAB_LABELS[t]}
              {t === 'transit' && itemsTransit.length > 0 && (
                <span className={`ml-1.5 text-[11px] px-1.5 py-0.5 rounded-full font-semibold ${tab === t ? 'bg-white/20 text-white' : 'bg-blue-50 text-blue-600'}`}>
                  {itemsTransit.length}
                </span>
              )}
              {t === 'effective' && itemsEffective.length > 0 && (
                <span className={`ml-1.5 text-[11px] px-1.5 py-0.5 rounded-full font-semibold ${tab === t ? 'bg-white/20 text-white' : 'bg-emerald-50 text-emerald-600'}`}>
                  {itemsEffective.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Barre sélection / actions (figée) */}
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={selected.size === list.length && list.length > 0}
              onChange={toggleSelectAll}
              className="w-4 h-4 rounded border-gray-300 accent-blue-600 cursor-pointer"
            />
            <span className="text-xs text-gray-500 font-medium">
              {selected.size > 0 ? `${selected.size} sélectionné${selected.size > 1 ? 's' : ''}` : 'Tout sélectionner'}
            </span>
          </label>
          <div className="ml-auto flex items-center gap-2">
            {tab === 'transit' && (
              <button
                type="button"
                onClick={bulkMarkReceived}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold hover:bg-emerald-100"
              >
                <ArrowRightCircle size={14} /> Marquer reçu
              </button>
            )}
            {tab === 'out' && (
              <button
                type="button"
                onClick={bulkMarkIn}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold hover:bg-blue-100"
              >
                <RotateCcw size={14} /> Remettre en stock
              </button>
            )}
            <button
              type="button"
              onClick={bulkDelete}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-100"
            >
              <Trash2 size={13} /> Supprimer
            </button>
          </div>
        </div>
      </div>

      {/* ── Contenu défilable ── */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw size={24} className="animate-spin text-blue-400" />
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <Warehouse size={48} className="mx-auto mb-3 opacity-30" />
            <p className="text-base font-medium">
              {tab === 'transit' ? 'Aucun article en transit'
                : tab === 'effective' ? 'Aucun article en stock'
                : 'Aucun article vendu'}
            </p>
            <p className="text-sm mt-1">
              {tab === 'transit'
                ? 'Les articles achetés apparaissent ici avant réception. Marque-les reçus pour les passer en stock.'
                : tab === 'effective'
                ? "Les articles reçus physiquement apparaissent ici. Ils passent automatiquement dans « Vendus » dès qu'une vente liée est expédiée."
                : "Articles liés à une vente expédiée ou marqués vendus manuellement."}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {is_free_plan && list.length > 10 && (
              <div className="relative mb-2">
                <div className="blur-sm pointer-events-none select-none space-y-2">
                  {list.slice(0, list.length - 10).map(i => (
                    <StockCard key={i.id} item={i} variant={tab} selected={false} onToggleSelect={() => {}} onUpdate={async () => {}} onDelete={async () => {}} />
                  ))}
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 text-center shadow-xl border border-gray-100 mx-4 max-w-xs">
                    <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-3">
                      <Lock size={22} className="text-blue-500" />
                    </div>
                    <h4 className="font-bold text-gray-900 mb-1">Limite gratuite atteinte</h4>
                    <p className="text-xs text-gray-500 mb-4">Passez à Starter pour un CRM illimité.</p>
                    <button onClick={onUpgrade} className="px-5 py-2 rounded-xl bg-gradient-to-r from-blue-500 to-cyan-600 text-white text-sm font-semibold hover:opacity-90 transition-all shadow">
                      Voir les plans →
                    </button>
                  </div>
                </div>
              </div>
            )}
            {(is_free_plan && list.length > 10 ? list.slice(list.length - 10) : list).map(i => (
              <StockCard
                key={i.id}
                item={i}
                variant={tab}
                selected={selected.has(i.id)}
                onToggleSelect={toggleSelect}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {showAddModal && <AddStockModal onClose={() => setShowAddModal(false)} onSaved={loadItems} initialStatus={tab === 'transit' ? 'in_transit' : 'in_stock'} />}

      {/* Popup livraison ambiguë */}
      {activePending && (
        <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={18} className="text-amber-500" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Livraison ambiguë</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  Vinted indique que <span className="font-semibold text-gray-700">« {activePending.item_title} »</span> a été livré,
                  mais {activePending.matching_items.length} articles en transit portent ce nom exactement.
                  Lequel a été reçu ?
                </p>
              </div>
            </div>
            <div className="px-6 py-4 space-y-2">
              {activePending.matching_items.map((item, idx) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => resolvePending(activePending.id, item.id)}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all text-left"
                >
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Article #{idx + 1}</p>
                    {item.purchase_price != null && (
                      <p className="text-xs text-gray-500">Achat : {item.purchase_price.toFixed(2)} €</p>
                    )}
                    {item.added_at && (
                      <p className="text-xs text-gray-400">Ajouté le {new Date(item.added_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                    )}
                  </div>
                  <Check size={16} className="text-blue-500 flex-shrink-0" />
                </button>
              ))}
            </div>
            <div className="px-6 pb-5">
              <button
                type="button"
                onClick={() => dismissPending(activePending.id)}
                className="w-full py-2 rounded-xl border border-gray-200 text-gray-500 text-sm hover:bg-gray-50"
              >
                Ignorer
              </button>
              {pendingReceives.length > 1 && (
                <p className="text-center text-xs text-gray-400 mt-2">{pendingReceives.length - 1} autre(s) livraison(s) en attente</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmStock;
